import { type ComponentProps, type FC, useEffect, useMemo } from "react";
import { type Salaries } from "@userFrontend/features/publication/components/Salaries";
import { generateGaLabel } from "@userFrontend/features/publication/components/WorkDetailOtherWorkCassettes/module";
import {
  APP_OTHER_JOBS_ACTION,
  APP_OTHER_JOBS_SHOW_ACTION,
  GA_OTHER_JOBS_ACTION,
} from "@userFrontend/features/publication/components/WorkDetailOtherWorkCassettes/constants";
import { sendAppWebhook } from "@userFrontend/utils/sendAppWebhook";
import { useInView } from "react-intersection-observer";
import { NoButtonWorkCassette } from "@root/userFrontend/components/NoButtonWorkCassette";

type Props = {
  detailWorkCode: string;
  docId: string;
  url: string;
  imgSrc: string;
  shopName: string;
  title: string;
  shopAddress: {
    city: string;
    prefecture: string;
    station: string;
  };
  hasBorder?: boolean;
  isShowLocationOnlyOneLine?: boolean;
} & ComponentProps<typeof Salaries>;

export const Cassette: FC<Props> = ({
  detailWorkCode,
  docId,
  url,
  imgSrc,
  shopName,
  title,
  employmentAndSalaryType,
  shopAddress: { city, prefecture, station },
  hasBorder = false,
  isShowLocationOnlyOneLine = false,
}) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const matchedClientShopId = /.*_(.*)$/.exec(docId);
  const appEventCommonMessage = useMemo(
    () => ({
      work_code: detailWorkCode,
      sid: matchedClientShopId ? matchedClientShopId[0] : "",
    }),
    [detailWorkCode, matchedClientShopId],
  );
  const gaLabel = generateGaLabel(url, docId);

  const handleOnClickLink = () => {
    sendAppWebhook({
      iosEventMessage: {
        ...appEventCommonMessage,
        message: "firebase",
        contentID: APP_OTHER_JOBS_ACTION,
      },
      androidEventName: "firebase",
      androidEventMessage: JSON.stringify({
        ...appEventCommonMessage,
        content_id: APP_OTHER_JOBS_ACTION,
      }),
    });
  };

  useEffect(() => {
    if (inView) {
      sendAppWebhook({
        iosEventMessage: {
          ...appEventCommonMessage,
          message: "firebase",
          contentID: APP_OTHER_JOBS_SHOW_ACTION,
        },
        androidEventName: "firebase",
        androidEventMessage: JSON.stringify({
          ...appEventCommonMessage,
          content_id: APP_OTHER_JOBS_SHOW_ACTION,
        }),
      });
    }
  }, [appEventCommonMessage, inView]);

  return (
    <NoButtonWorkCassette
      gaAction={GA_OTHER_JOBS_ACTION}
      gaLabel={gaLabel}
      onClickLink={handleOnClickLink}
      ref={ref}
      url={url}
      imgSrc={imgSrc}
      shopName={shopName}
      title={title}
      employmentAndSalaryType={employmentAndSalaryType}
      shopAddress={{ city, prefecture, station }}
      hasBorder={hasBorder}
      isShowLocationOnlyOneLine={isShowLocationOnlyOneLine}
    />
  );
};
